import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import axios from 'axios';

const PageEditor = ({ page }) => {
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await axios.get(`/api/pages/${page}`);
        setContent(response.data);
        setTitle(response.data.title);
      } catch (err) {
        console.error('Error loading page content:', err);
        setError('Failed to load page content');
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [page]);

  const handleSave = async () => {
    if (!title.trim() || !content?.content?.trim()) {
      setError('Title and content are required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const sanitizedContent = sanitizeContent(content.content);
      await axios.put(`/api/pages/${page}`, {
        title: title.trim(),
        content: sanitizedContent
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving page content:', err);
      setError('Failed to save page content');
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  // Sanitize content before saving
  const sanitizeContent = (content) => {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    });
  };

  if (loading) {
    return <div className="loading">Loading page content...</div>;
  }

  if (error && !content) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="page-editor">
      <h2>Edit {page.charAt(0).toUpperCase() + page.slice(1)} Page</h2>

      <div className="editor-section">
        <label htmlFor="title">Page Title:</label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter page title"
          className="title-input"
        />
      </div>

      <div className="editor-section">
        <label>Page Content:</label>
        <ReactQuill
          theme="snow"
          value={content?.content || ''}
          onChange={(value) => setContent({ ...content, content: value })}
          modules={modules}
          placeholder="Enter page content..."
        />
      </div>

      <div className="editor-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">Page saved successfully!</div>}
      </div>

      <style jsx>{`
        .page-editor {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .editor-section {
          margin-bottom: 20px;
        }

        .editor-section label {
          display: block;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .title-input {
          width: 100%;
          padding: 12px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
        }

        .editor-actions {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .save-btn {
          background: #1976d2;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          transition: background 0.3s;
        }

        .save-btn:hover:not(:disabled) {
          background: #1565c0;
        }

        .save-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .error-message {
          color: #d32f2f;
          margin-top: 10px;
          padding: 10px;
          background: #ffebee;
          border-radius: 4px;
        }

        .success-message {
          color: #2e7d32;
          margin-top: 10px;
          padding: 10px;
          background: #e8f5e8;
          border-radius: 4px;
        }

        .loading, .error {
          text-align: center;
          padding: 40px;
          font-size: 18px;
        }
      `}</style>
    </div>
  );
};

export default PageEditor;